/* ===========================================================
   editor/EquationDialog.jsx — "Insert equation" with a live preview

   Casual users get buttons for the common pieces (fraction, square
   root, powers, Greek letters…) that drop LaTeX in for them, and see
   the result update as they go. Developers can just type LaTeX.
   =========================================================== */

import { useEffect, useRef, useState } from 'preact/hooks';
import { Dialog, Button, openModal } from '../ui/ui.jsx';
import { renderMath, loadKatex } from '../lib/katex.js';

const PIECES = [
  { label: 'a/b', title: 'Fraction', tex: '\\frac{a}{b}' },
  { label: 'x²', title: 'Power', tex: 'x^{2}' },
  { label: 'x₁', title: 'Subscript', tex: 'x_{1}' },
  { label: '√x', title: 'Square root', tex: '\\sqrt{x}' },
  { label: 'ⁿ√x', title: 'Nth root', tex: '\\sqrt[n]{x}' },
  { label: '±', title: 'Plus or minus', tex: '\\pm ' },
  { label: '×', title: 'Times', tex: '\\times ' },
  { label: '÷', title: 'Divided by', tex: '\\div ' },
  { label: '≤', title: 'Less or equal', tex: '\\le ' },
  { label: '≥', title: 'Greater or equal', tex: '\\ge ' },
  { label: '≠', title: 'Not equal', tex: '\\ne ' },
  { label: '≈', title: 'About equal', tex: '\\approx ' },
  { label: 'π', title: 'Pi', tex: '\\pi ' },
  { label: 'θ', title: 'Theta', tex: '\\theta ' },
  { label: 'α', title: 'Alpha', tex: '\\alpha ' },
  { label: 'β', title: 'Beta', tex: '\\beta ' },
  { label: 'Δ', title: 'Delta', tex: '\\Delta ' },
  { label: '∞', title: 'Infinity', tex: '\\infty ' },
  { label: '°', title: 'Degrees', tex: '^{\\circ}' },
  { label: 'Σ', title: 'Sum', tex: '\\sum_{i=1}^{n} ' },
  { label: '∫', title: 'Integral', tex: '\\int_{a}^{b} ' },
  { label: 'lim', title: 'Limit', tex: '\\lim_{x \\to 0} ' },
  { label: '( )', title: 'Big brackets', tex: '\\left( x \\right)' },
  { label: '→', title: 'Arrow', tex: '\\rightarrow ' },
];

function EquationBox({ latex: start, display: startDisplay, done }) {
  const [latex, setLatex] = useState(start || '');
  const [display, setDisplay] = useState(startDisplay);
  const preview = useRef();
  const input = useRef();
  useEffect(() => { loadKatex().catch(() => {}); }, []);
  useEffect(() => { if (preview.current) renderMath(preview.current, latex, true); }, [latex]);

  const insert = tex => {
    const el = input.current;
    const a = el.selectionStart ?? latex.length, b = el.selectionEnd ?? latex.length;
    const next = latex.slice(0, a) + tex + latex.slice(b);
    setLatex(next);
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = a + tex.length; });
  };

  return (
    <Dialog title={start ? 'Edit equation' : 'Insert equation'} onClose={() => done(null)} wide
      actions={<>
        {start && <Button kind="danger-plain" onClick={() => done({ latex: '', display })}>Remove</Button>}
        <span class="grow" />
        <Button onClick={() => done(null)}>Cancel</Button>
        <Button kind="primary" onClick={() => done({ latex, display })}>{start ? 'Save' : 'Insert'}</Button>
      </>}>
      <div class="eq-preview" aria-live="polite" ref={preview} />
      <div class="eq-pieces" role="group" aria-label="Common pieces">
        {PIECES.map(pc => <button type="button" class="eq-piece" title={pc.title} aria-label={pc.title} onClick={() => insert(pc.tex)}>{pc.label}</button>)}
      </div>
      <label class="field">
        <span>Equation (LaTeX)</span>
        <textarea ref={input} class="mono" rows={3} autofocus value={latex} spellcheck={false}
          placeholder="e.g. a^2 + b^2 = c^2" onInput={e => setLatex(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) done({ latex, display }); }} />
      </label>
      <label class="check">
        <input type="checkbox" checked={display} onChange={e => setDisplay(e.target.checked)} />
        <span>Put it on its own line</span>
      </label>
    </Dialog>
  );
}

/** Resolves to { latex, display } or null. */
export const equationDialog = ({ latex = '', display = true } = {}) => openModal(done => <EquationBox latex={latex} display={display} done={done} />);
